// packages/web/src/pages/PrivacyPolicyPage.tsx
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';
import { LoginDialog } from '../components/Auth/LoginDialog';
import { useAuth } from '../hooks/useAuth';

const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
});

const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#fafafa',
  padding: theme.spacing(8, 0, 6, 0),
  textAlign: 'center',
  borderBottom: '1px solid #e5e5e5',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0, 4, 0),
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  lineHeight: 1.2,
  marginBottom: theme.spacing(2),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  lineHeight: 1.7,
  fontWeight: 400,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  marginTop: theme.spacing(5),
  marginBottom: theme.spacing(2.5),
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
  '&:first-of-type': {
    marginTop: 0,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const SubsectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1.5),
  color: '#1a1a1a',
}));

const BodyText = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  lineHeight: 1.8,
  fontWeight: 400,
  marginBottom: theme.spacing(2),
}));

const BulletList = styled('ul')(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  marginBottom: theme.spacing(2),
  '& li': {
    fontSize: '1rem',
    color: '#666',
    lineHeight: 1.8,
    marginBottom: theme.spacing(1),
  },
}));

const PrivacyPolicyPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  return (
    <PageContainer>
      <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />

      <HeroSection>
        <Container maxWidth="md">
          <PageTitle>Privacy Policy</PageTitle>
          <PageSubtitle>
            Last updated: January 1, 2025
          </PageSubtitle>
        </Container>
      </HeroSection>

      <ContentSection>
        <Container maxWidth="md">
          <BodyText>
            This Privacy Policy describes how Sched Tennis ("we," "our," or "us") collects, uses, 
            and protects your information when you use our tennis scheduling platform.
          </BodyText>

          <SectionTitle>1. Information We Collect</SectionTitle>
          
          <SubsectionTitle>1.1 Information You Provide</SubsectionTitle>
          <BodyText>When you use Sched Tennis, you may provide us with:</BodyText>
          <BulletList>
            <li>Account information: Name, email address (via Google Sign-In)</li>
            <li>Profile information: Player skill level, preferred courts, location</li>
            <li>Scheduling information: Available time slots, court preferences, match history</li>
            <li>Communication data: Messages and challenges sent to other players</li>
          </BulletList>

          <SubsectionTitle>1.2 Information We Collect Automatically</SubsectionTitle>
          <BodyText>When you use our platform, we automatically collect:</BodyText>
          <BulletList>
            <li>Device information: Browser type, operating system, device identifiers</li>
            <li>Usage information: Pages visited, features used, time spent on the platform</li>
            <li>Log information: IP address, access times, error logs</li>
            <li>Location data: Approximate location for court recommendations (if permitted)</li>
          </BulletList>

          <SubsectionTitle>1.3 Information from Third Parties</SubsectionTitle>
          <BulletList>
            <li>Google Account: When you sign in with Google, we receive your name, email address, and profile picture</li>
          </BulletList>

          <SectionTitle>2. How We Use Your Information</SectionTitle>
          <BodyText>We use the information we collect to:</BodyText>
          <BulletList>
            <li>Provide and maintain our tennis scheduling services</li>
            <li>Match you with compatible players based on skill level, location, and availability</li>
            <li>Send notifications about match requests, confirmations, and updates</li>
            <li>Improve and personalize your experience on the platform</li>
            <li>Analyze usage patterns to enhance platform functionality</li>
            <li>Detect and prevent fraud, abuse, and security issues</li>
            <li>Comply with legal obligations and enforce our Terms of Service</li>
            <li>Communicate with you about platform updates and features</li>
          </BulletList>

          <SectionTitle>3. How We Share Your Information</SectionTitle>
          
          <SubsectionTitle>3.1 With Other Users</SubsectionTitle>
          <BodyText>
            When you publish your schedule or accept a match challenge, certain information becomes 
            visible to other users:
          </BodyText>
          <BulletList>
            <li>Your name and profile information</li>
            <li>Your published availability and court locations</li>
            <li>Your skill level and match history (if you choose to share it)</li>
          </BulletList>

          <SubsectionTitle>3.2 With Service Providers</SubsectionTitle>
          <BodyText>
            We may share information with third-party service providers who help us operate the platform:
          </BodyText>
          <BulletList>
            <li>Cloud hosting and storage providers</li>
            <li>Authentication services (Google)</li>
            <li>Analytics and performance monitoring services</li>
            <li>Email and notification services</li>
          </BulletList>

          <SubsectionTitle>3.3 For Legal Reasons</SubsectionTitle>
          <BodyText>
            We may disclose your information if required by law or to:
          </BodyText>
          <BulletList>
            <li>Comply with legal processes or government requests</li>
            <li>Enforce our Terms of Service</li>
            <li>Protect the rights, property, or safety of Sched Tennis, our users, or others</li>
            <li>Investigate fraud or security issues</li>
          </BulletList>

          <SectionTitle>4. Data Security</SectionTitle>
          <BodyText>
            We implement appropriate technical and organizational measures to protect your personal information:
          </BodyText>
          <BulletList>
            <li>Encryption of data in transit and at rest</li>
            <li>Secure authentication using Google OAuth</li>
            <li>Regular security audits and updates</li>
            <li>Access controls limiting who can view your information</li>
            <li>Monitoring for suspicious activity and security threats</li>
          </BulletList>
          <BodyText>
            However, no method of transmission over the internet is 100% secure. While we strive to 
            protect your information, we cannot guarantee absolute security.
          </BodyText>

          <SectionTitle>5. Your Rights and Choices</SectionTitle>
          <BodyText>You have the following rights regarding your personal information:</BodyText>
          <BulletList>
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information through your profile settings</li>
            <li><strong>Deletion:</strong> Request deletion of your account and personal information</li>
            <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
            <li><strong>Opt-out:</strong> Unsubscribe from promotional emails (match notifications will still be sent)</li>
            <li><strong>Privacy settings:</strong> Control who can see your schedule (public or private link only)</li>
          </BulletList>
          <BodyText>
            To exercise these rights, please contact us through our Contact page or email us directly.
          </BodyText>

          <SectionTitle>6. Data Retention</SectionTitle>
          <BodyText>We retain your information for as long as necessary to provide our services:</BodyText>
          <BulletList>
            <li>Account information: Until you delete your account</li>
            <li>Match history: Retained for your records and platform analytics</li>
            <li>Usage logs: Typically 90 days for security and analytics purposes</li>
            <li>Legal requirements: Some data may be retained longer to comply with legal obligations</li>
          </BulletList>
          <BodyText>
            When you delete your account, we will remove your personal information, though some data 
            may remain in backup systems for a limited time.
          </BodyText>

          <SectionTitle>7. Cookies and Tracking Technologies</SectionTitle>
          <BodyText>We use cookies and similar technologies to:</BodyText>
          <BulletList>
            <li><strong>Essential cookies:</strong> Required for authentication and platform functionality</li>
            <li><strong>Analytics cookies:</strong> Help us understand how users interact with the platform</li>
            <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
          </BulletList>
          <BodyText>
            You can control cookies through your browser settings, but disabling certain cookies may 
            affect platform functionality.
          </BodyText>

          <SectionTitle>8. Children's Privacy</SectionTitle>
          <BodyText>
            Sched Tennis is not intended for users under the age of 13. We do not knowingly collect 
            personal information from children under 13. If we become aware that we have collected 
            information from a child under 13, we will take steps to delete that information.
          </BodyText>

          <SectionTitle>9. International Data Transfers</SectionTitle>
          <BodyText>
            Your information may be transferred to and processed in countries other than your country 
            of residence. These countries may have different data protection laws. By using Sched Tennis, 
            you consent to the transfer of your information to these countries.
          </BodyText>

          <SectionTitle>10. Changes to This Privacy Policy</SectionTitle>
          <BodyText>
            We may update this Privacy Policy from time to time to reflect changes in our practices or 
            for legal, operational, or regulatory reasons. We will notify you of significant changes by:
          </BodyText>
          <BulletList>
            <li>Posting the updated policy on this page with a new "Last updated" date</li>
            <li>Sending an email notification to your registered email address</li>
            <li>Displaying a notice on the platform</li>
          </BulletList>
          <BodyText>
            Your continued use of Sched Tennis after changes are posted constitutes acceptance of the 
            updated Privacy Policy.
          </BodyText>

          <SectionTitle>11. Contact Us</SectionTitle>
          <BodyText>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy 
            practices, please contact us:
          </BodyText>
          <BodyText sx={{ ml: 2, fontWeight: 500 }}>
            Sched Tennis Privacy Team<br />
            Email: privacy@schedtennis.com
          </BodyText>
          <BodyText>
            We will respond to your inquiry within 30 days.
          </BodyText>
        </Container>
      </ContentSection>

      <Footer />

      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
      />
    </PageContainer>
  );
};

export default PrivacyPolicyPage;